import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Loader2, ShieldCheck, Users, FileCheck2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { user, login } = useAuth();
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const r = await login(loginId.trim(), password);
    setLoading(false);
    if (r.ok) navigate("/");
    else setError(r.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-blue-600 text-white p-12 relative overflow-hidden">
        <div className="absolute -right-20 -top-20 w-96 h-96 rounded-full bg-blue-500/40" />
        <div className="absolute -left-16 bottom-10 w-72 h-72 rounded-full bg-blue-700/40" />
        <div className="relative flex items-center gap-3">
          <img src="/wcc-icon.png" alt="WCC" className="w-11 h-11 rounded-xl bg-white p-1 object-contain" />
          <span className="text-lg font-bold">Welfy Care Campus</span>
        </div>
        <div className="relative">
          <h1 className="text-4xl font-bold leading-snug">障がい福祉の研修・<br />法定管理を、ひとつに。</h1>
          <p className="mt-4 text-blue-100 leading-relaxed">
            研修・委員会・訓練・修了証・年間計画・法定記録・実地指導対応を一元管理する<br />クラウド型SaaS。
          </p>
          <div className="mt-8 space-y-3">
            {[["法定研修・委員会・訓練を一元管理", ShieldCheck], ["職員の受講状況・修了証を可視化", Users], ["実地指導・監査にワンクリック対応", FileCheck2]].map(([t, I]) => (
              <div key={t} className="flex items-center gap-3 text-blue-50">
                <I className="w-5 h-5 shrink-0" /> <span className="text-sm">{t}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-blue-200">© 2026 Welfy Care Campus</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <img src="/wcc-logo.png" alt="Welfy Care Campus" className="h-[7.5rem] sm:h-[9rem] object-contain mb-6 mx-auto block" />
          <h2 className="text-2xl font-bold text-slate-900">ログイン</h2>
          <p className="text-sm text-slate-500 mt-1 mb-6">ログインIDとパスワードを入力してください</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="login_id">ログインID</Label>
              <Input id="login_id" data-testid="login-id-input" value={loginId} onChange={(e) => setLoginId(e.target.value)}
                placeholder="admin" className="mt-1.5" autoComplete="username" required />
            </div>
            <div>
              <Label htmlFor="password">パスワード</Label>
              <Input id="password" data-testid="login-password-input" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1.5"
                autoComplete="current-password" required />
            </div>
            {error && <div data-testid="login-error" className="text-sm text-rose-600 bg-rose-50 rounded-lg px-3 py-2">{error}</div>}
            <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "ログイン"}
            </Button>
          </form>

          <div className="mt-6 text-xs text-slate-400 bg-slate-100 rounded-lg p-3 leading-relaxed">
            <div className="font-medium text-slate-500 mb-1">デモアカウント</div>
            運営管理者：superadmin ／ 管理者：admin ／ 事業所：office1 ／ 職員：staff1・staff2<br />
            パスワード：Welfy2026!
          </div>
        </div>
      </div>
    </div>
  );
}
